<template>
    <div class="max-w-7xl mx-auto md:px-4 sm:px-6 md:px-8">
        <div class="flex flex-wrap justify-end space-x-2 mt-5">
            <AppButton v-for="link in meta.links.topLinks" :href="link.url" :key="link.url">
                {{ link.text }}
            </AppButton>
        </div>
        <div class="md:grid md:grid-cols-3 md:gap-6">
            <div>
                <CardPanel class="mt-5">
                    <img
                        :src="student.avatar_or_default"
                        alt="photo"
                        class="mx-auto rounded-full h-32 w-32 border-4 border-white"
                    />

                    <div class="flex flex-col justify-center items-center">
                        <div class="text-lg leading-6 font-medium text-gray-900">
                            {{ student.name_or_nickname }}
                        </div>
                        <div class="leading-6 font-base text-gray-600">
                            {{ $t('messages.student') }}
                        </div>
                        <div class="leading-6 font-base text-gray-600 flex item-center space-x-2">
                            <CanTakeStudent
                                type="student"
                                :value="student.currentStudentFile.can_leave_by_themselves"
                            />
                            <div>
                                {{
                                    student.currentStudentFile.can_leave_by_themselves
                                        ? $t('messages.can_leave_by_themselves')
                                        : $t('messages.cannot_leave_by_themselves')
                                }}
                            </div>
                        </div>
                    </div>
                </CardPanel>

                <StudentProfileJustifyAbsences v-if="attendance" :attendance="attendance" />

                <CardPanel class="mt-5">
                    <div v-if="meta.can.impersonate">
                        <AppButton :href="meta.links.impersonate" desired-action>
                            <svg
                                class="w-6 h-6"
                                fill="none"
                                stroke="currentColor"
                                viewBox="0 0 24 24"
                                xmlns="http://www.w3.org/2000/svg"
                            >
                                <path
                                    stroke-linecap="round"
                                    stroke-linejoin="round"
                                    stroke-width="2"
                                    d="M11 16l-4-4m0 0l4-4m-4 4h14m-5 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h7a3 3 0 013 3v1"
                                ></path>
                            </svg>
                        </AppButton>
                    </div>
                    <div class="mt-5">
                        <div class="flex gap-x-2">
                            <InertiaLink
                                method="patch"
                                as="button"
                                :href="toggles.monitoring_student.url"
                                preserve-scroll
                                :class="toggles.monitoring_student.value ? 'bg-indigo-600' : 'bg-gray-200'"
                                :only="['toggles']"
                                class="relative inline-flex shrink-0 h-6 w-11 border-2 border-transparent rounded-full cursor-pointer transition-colors ease-in-out duration-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
                                role="switch"
                                aria-checked="false"
                            >
                                <span class="sr-only">role</span>
                                <span
                                    :class="toggles.monitoring_student.value ? 'translate-x-5' : 'translate-x-0'"
                                    aria-hidden="true"
                                    class="translate-x-0 pointer-events-none inline-block h-5 w-5 rounded-full bg-white shadow ring-0 transition ease-in-out duration-200"
                                ></span>
                            </InertiaLink>

                            <span>{{ $t('messages.monitor_student') }}</span>
                            <span class="inline-block">
                                <Tippy>
                                    <IconHelpCircle
                                        class="h-6 w-6 text-gray-400 group-hover:text-gray-300 group-focus:text-gray-300 transition ease-in-out duration-150"
                                    />
                                    <template #content>
                                        <span>{{ $t('messages.monitor_student_explanation') }}</span>
                                    </template>
                                </Tippy>
                            </span>
                        </div>
                    </div>
                </CardPanel>

                <CardPanel class="mt-5" :overflow-hidden="false">
                    <template v-slot:title>
                        {{ $t('messages.info_for_teachers') }}
                    </template>

                    <ul class="list-inside">
                        <li>
                            <template v-if="student.absenteeism">
                                <div>
                                    {{ student.absenteeism.percentageString }}
                                    {{ $t('messages.unjustified') }}
                                </div>
                                <div>
                                    {{ student.absenteeism.percentageJustifiedString }}
                                    {{ $t('messages.justified') }}
                                </div>
                                <div>
                                    {{ student.absenteeism.presentString }}
                                    {{ $t('messages.present') }}
                                </div>
                            </template>
                            <DropdownButton class="mt-1">
                                <template v-slot:text>
                                    <svg
                                        viewBox="0 0 20 20"
                                        fill="currentColor"
                                        class="clipboard-check mr-3 w-4 h-4 inline-block text-gray-500"
                                    >
                                        <path d="M9 2a1 1 0 000 2h2a1 1 0 100-2H9z"></path>
                                        <path
                                            fill-rule="evenodd"
                                            d="M4 5a2 2 0 012-2 3 3 0 003 3h2a3 3 0 003-3 2 2 0 012 2v11a2 2 0 01-2 2H6a2 2 0 01-2-2V5zm9.707 5.707a1 1 0 00-1.414-1.414L9 12.586l-1.293-1.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
                                            clip-rule="evenodd"
                                        ></path>
                                    </svg>
                                    {{ $t('messages.attendance') }}
                                </template>
                                <template v-slot:content>
                                    <a
                                        :href="meta.links.student_attendance_index"
                                        class="block px-4 py-2 text-sm leading-5 text-gray-700 hover:bg-gray-100 hover:text-gray-900 focus:outline-none focus:bg-gray-100 focus:text-gray-900"
                                        role="menuitem"
                                    >
                                        {{ $t('messages.attendance_and_absences_review') }}
                                    </a>
                                    <a
                                        :href="meta.links.student_attendance_calendar"
                                        class="block px-4 py-2 text-sm leading-5 text-gray-700 hover:bg-gray-100 hover:text-gray-900 focus:outline-none focus:bg-gray-100 focus:text-gray-900"
                                        role="menuitem"
                                    >
                                        {{ $t('messages.calendar') }}
                                    </a>
                                </template>
                            </DropdownButton>
                        </li>

                        <li>
                            <DropdownButton class="mt-1">
                                <template v-slot:text>
                                    <svg
                                        viewBox="0 0 20 20"
                                        fill="currentColor"
                                        class="clipboard-list mr-3 w-4 h-4 inline-block text-gray-500"
                                    >
                                        <path d="M9 2a1 1 0 000 2h2a1 1 0 100-2H9z"></path>
                                        <path
                                            fill-rule="evenodd"
                                            d="M4 5a2 2 0 012-2 3 3 0 003 3h2a3 3 0 003-3 2 2 0 012 2v11a2 2 0 01-2 2H6a2 2 0 01-2-2V5zm3 4a1 1 0 000 2h.01a1 1 0 100-2H7zm3 0a1 1 0 000 2h3a1 1 0 100-2h-3zm-3 4a1 1 0 100 2h.01a1 1 0 100-2H7zm3 0a1 1 0 100 2h3a1 1 0 100-2h-3z"
                                            clip-rule="evenodd"
                                        ></path>
                                    </svg>
                                    {{ $tc('messages.fouls', 2) }}
                                </template>
                                <template v-slot:content>
                                    <a
                                        :href="meta.links.foul_create"
                                        class="block px-4 py-2 text-sm leading-5 text-gray-700 hover:bg-gray-100 hover:text-gray-900 focus:outline-none focus:bg-gray-100 focus:text-gray-900"
                                        role="menuitem"
                                    >
                                        {{ $t('messages.issue_foul') }}
                                    </a>
                                    <a
                                        :href="meta.links.foul_student_summary"
                                        class="block px-4 py-2 text-sm leading-5 text-gray-700 hover:bg-gray-100 hover:text-gray-900 focus:outline-none focus:bg-gray-100 focus:text-gray-900"
                                        role="menuitem"
                                    >
                                        {{ $t('messages.fouls_summary') }}
                                    </a>
                                </template>
                            </DropdownButton>
                        </li>
                        <li>
                            <DropdownButton class="mt-1">
                                <template v-slot:text>
                                    <svg
                                        viewBox="0 0 20 20"
                                        fill="currentColor"
                                        class="clipboard-list mr-3 w-4 h-4 inline-block text-gray-500"
                                    >
                                        <path d="M9 2a1 1 0 000 2h2a1 1 0 100-2H9z"></path>
                                        <path
                                            fill-rule="evenodd"
                                            d="M4 5a2 2 0 012-2 3 3 0 003 3h2a3 3 0 003-3 2 2 0 012 2v11a2 2 0 01-2 2H6a2 2 0 01-2-2V5zm3 4a1 1 0 000 2h.01a1 1 0 100-2H7zm3 0a1 1 0 000 2h3a1 1 0 100-2h-3zm-3 4a1 1 0 100 2h.01a1 1 0 100-2H7zm3 0a1 1 0 100 2h3a1 1 0 100-2h-3z"
                                            clip-rule="evenodd"
                                        ></path>
                                    </svg>
                                    {{ $tc('messages.family_interviews', 2) }}
                                </template>
                                <template v-slot:content>
                                    <a
                                        :href="meta.links.family_interviews_create"
                                        class="block px-4 py-2 text-sm leading-5 text-gray-700 hover:bg-gray-100 hover:text-gray-900 focus:outline-none focus:bg-gray-100 focus:text-gray-900"
                                        role="menuitem"
                                    >
                                        {{ $t('messages.create') }}
                                    </a>
                                    <a
                                        :href="meta.links.family_interviews_index"
                                        class="block px-4 py-2 text-sm leading-5 text-gray-700 hover:bg-gray-100 hover:text-gray-900 focus:outline-none focus:bg-gray-100 focus:text-gray-900"
                                        role="menuitem"
                                    >
                                        {{ $t('messages.list') }}
                                    </a>
                                </template>
                            </DropdownButton>
                        </li>
                        <li>
                            <DropdownButton class="mt-1">
                                <template v-slot:text>
                                    <svg
                                        viewBox="0 0 20 20"
                                        fill="currentColor"
                                        class="clipboard-copy mr-3 w-4 h-4 inline-block text-gray-500"
                                    >
                                        <path d="M8 2a1 1 0 000 2h2a1 1 0 100-2H8z"></path>
                                        <path
                                            d="M3 5a2 2 0 012-2 3 3 0 003 3h2a3 3 0 003-3 2 2 0 012 2v6h-4.586l1.293-1.293a1 1 0 00-1.414-1.414l-3 3a1 1 0 000 1.414l3 3a1 1 0 001.414-1.414L10.414 13H15v3a2 2 0 01-2 2H5a2 2 0 01-2-2V5zM15 11h2a1 1 0 110 2h-2v-2z"
                                        ></path>
                                    </svg>
                                    {{ $tc('messages.intervention_records', 2) }}
                                </template>
                                <template v-slot:content>
                                    <a
                                        :href="meta.links.intervention_records_create"
                                        class="block px-4 py-2 text-sm leading-5 text-gray-700 hover:bg-gray-100 hover:text-gray-900 focus:outline-none focus:bg-gray-100 focus:text-gray-900"
                                        role="menuitem"
                                    >
                                        {{ $t('messages.create') }}
                                    </a>
                                    <a
                                        :href="meta.links.intervention_records_index"
                                        class="block px-4 py-2 text-sm leading-5 text-gray-700 hover:bg-gray-100 hover:text-gray-900 focus:outline-none focus:bg-gray-100 focus:text-gray-900"
                                        role="menuitem"
                                    >
                                        {{ $t('messages.list') }}
                                    </a>
                                </template>
                            </DropdownButton>
                        </li>
                        <li v-if="meta.links.student_payments_index">
                            <AppButton element="InertiaLink" class="mt-1" :href="meta.links.student_payments_index">
                                <svg
                                    viewBox="0 0 20 20"
                                    fill="currentColor"
                                    class="clipboard-check mr-3 w-4 h-4 inline-block text-gray-500"
                                >
                                    <path d="M9 2a1 1 0 000 2h2a1 1 0 100-2H9z"></path>
                                    <path
                                        fill-rule="evenodd"
                                        d="M4 5a2 2 0 012-2 3 3 0 003 3h2a3 3 0 003-3 2 2 0 012 2v11a2 2 0 01-2 2H6a2 2 0 01-2-2V5zm9.707 5.707a1 1 0 00-1.414-1.414L9 12.586l-1.293-1.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
                                        clip-rule="evenodd"
                                    ></path>
                                </svg>
                                {{ $tc('messages.payments', 2) }}
                            </AppButton>
                        </li>
                        <li v-if="meta.can.see.grades_report" class="mt-1">
                            <DropdownButton class="relative -ml-px block text-gray-500">
                                <template v-slot:text>
                                    <IconPrinter class="-ml-0.5 mr-2 w-4 h-4" />
                                    {{ $t('messages.grades_report') }}
                                </template>
                                <template v-slot:content>
                                    <InertiaLink
                                        :href="meta.links.grades_report_student_index"
                                        class="block px-4 py-2 text-sm leading-5 text-gray-700 hover:bg-gray-100 hover:text-gray-900 focus:outline-none focus:bg-gray-100 focus:text-gray-900"
                                        role="menuitem"
                                    >
                                        {{ $tc('messages.comments', 2) }}
                                    </InertiaLink>

                                    <a
                                        v-for="link in meta.links.grades_report_links"
                                        :key="link.url"
                                        :href="link.url"
                                        target="_blank"
                                        class="block px-4 py-2 text-sm leading-5 text-gray-700 hover:bg-gray-100 hover:text-gray-900 focus:outline-none focus:bg-gray-100 focus:text-gray-900"
                                        role="menuitem"
                                    >
                                        <div class="flex gap-x-2">
                                            <span>{{ link.text }}</span>
                                            <span :title="link.explanation">
                                                <IconAlertTriangle
                                                    class="h-6 w-6"
                                                    :class="link.is_visible ? 'text-yellow-400' : 'text-gray-300'"
                                                />
                                            </span>
                                        </div>
                                    </a>
                                </template>
                            </DropdownButton>
                        </li>
                    </ul>
                </CardPanel>

                <CardPanel class="mt-5">
                    <InertiaLink :href="meta.links.contacts_index" class="text-indigo-500">
                        {{ $t('messages.contacts') }}
                    </InertiaLink>
                    <AppWell>
                        <div class="" v-if="student.directRelatives.length">
                            {{ $tc('messages.relatives', 2) }}
                            <ul class="mt-1">
                                <li v-for="relative in student.directRelatives" :key="relative.id">
                                    <AppCard class="mt-1 md:mt-3">
                                        <div class="flex items-center md:space-x-2">
                                            <CanTakeStudent :value="relative.can_take_student" />
                                            <template v-if="relative.links.show">
                                                <a :href="relative.links.show">
                                                    {{ relative.full_name }} ({{ relative.relationshipFromChildren }}) -
                                                </a>
                                            </template>
                                            <template v-else>
                                                {{ relative.full_name }} ({{ relative.relationshipFromChildren }}) -
                                            </template>

                                            <InertiaLink
                                                :href="relative.links.message"
                                                class="inline-block text-gray-400 rounded-full hover:bg-gray-100 hover:text-gray-500 focus:outline-none focus:ring focus:text-gray-500"
                                                aria-label="Notifications"
                                            >
                                                <svg
                                                    class="w-6 h-6"
                                                    fill="none"
                                                    stroke="currentColor"
                                                    viewBox="0 0 24 24"
                                                    xmlns="http://www.w3.org/2000/svg"
                                                >
                                                    <path
                                                        stroke-linecap="round"
                                                        stroke-linejoin="round"
                                                        stroke-width="2"
                                                        d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"
                                                    ></path>
                                                </svg>
                                            </InertiaLink>

                                            <a :href="relative.links.impersonate" v-if="relative.links.impersonate">
                                                <svg
                                                    class="w-6 h-6 inline-block text-gray-400"
                                                    fill="none"
                                                    stroke="currentColor"
                                                    viewBox="0 0 24 24"
                                                    xmlns="http://www.w3.org/2000/svg"
                                                >
                                                    <path
                                                        stroke-linecap="round"
                                                        stroke-linejoin="round"
                                                        stroke-width="2"
                                                        d="M11 16l-4-4m0 0l4-4m-4 4h14m-5 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h7a3 3 0 013 3v1"
                                                    ></path>
                                                </svg>
                                            </a>
                                        </div>
                                        <div>
                                            <PhonesList :phones="relative.phones" :with-columns="false" />
                                        </div>
                                        <div v-if="relative.lastAccess">
                                            <InertiaLink class="text-xs text-gray-400" :href="relative.lastAccess.url">
                                                {{ $t('messages.last_access') }}: {{ relative.lastAccess.text }}
                                            </InertiaLink>
                                        </div>
                                    </AppCard>
                                </li>
                            </ul>
                        </div>

                        <StudentProfileContactList :student="student" :contacts="student.contacts" />
                    </AppWell>
                </CardPanel>

                <CardPanel class="mt-5">
                    <h3>
                        {{ $t('messages.photo') }}
                    </h3>

                    <div v-if="student.mainProfilePhoto">
                        {{ $t('messages.delete_photo') }}

                        <InertiaSquareConfirmActionButton
                            :href="meta.links.avatar_delete"
                            danger
                            :confirm-text="$t('messages.delete')"
                            method="delete"
                            :title="$t('messages.delete_photo')"
                        >
                            <template v-slot:icon>
                                <svg viewBox="0 0 20 20" fill="currentColor" class="trash w-4 h-4">
                                    <path
                                        fill-rule="evenodd"
                                        d="M9 2a1 1 0 00-.894.553L7.382 4H4a1 1 0 000 2v10a2 2 0 002 2h8a2 2 0 002-2V6a1 1 0 100-2h-3.382l-.724-1.447A1 1 0 0011 2H9zM7 8a1 1 0 012 0v6a1 1 0 11-2 0V8zm5-1a1 1 0 00-1 1v6a1 1 0 102 0V8a1 1 0 00-1-1z"
                                        clip-rule="evenodd"
                                    ></path>
                                </svg>
                            </template>
                            {{ $t('messages.are_you_sure?') }}
                        </InertiaSquareConfirmActionButton>
                    </div>
                    <div v-else>
                        <div>
                            {{ $tc('messages.upload_photo', 2) }}
                        </div>
                        <AvatarUploader :endpoint="meta.links.avatar_store" />
                    </div>
                </CardPanel>

                <CardPanel class="mt-5" v-if="meta.can.have_gamification_avatar">
                    <h3>
                        {{ $t('messages.avatar') }}
                    </h3>

                    <div>
                        <div v-if="student.avatarProfilePhoto">
                            <img
                                :src="student.avatar_or_profile_photo"
                                alt="avatar"
                                class="mx-auto rounded-full h-32 w-32 border-4 border-white"
                            />

                            {{ $t('messages.delete_photo') }}

                            <InertiaSquareConfirmActionButton
                                :href="meta.links.gamification_avatar_delete"
                                danger
                                :confirm-text="$t('messages.delete')"
                                method="delete"
                                :title="$t('messages.delete_photo')"
                            >
                                <template v-slot:icon>
                                    <svg viewBox="0 0 20 20" fill="currentColor" class="trash w-4 h-4">
                                        <path
                                            fill-rule="evenodd"
                                            d="M9 2a1 1 0 00-.894.553L7.382 4H4a1 1 0 000 2v10a2 2 0 002 2h8a2 2 0 002-2V6a1 1 0 100-2h-3.382l-.724-1.447A1 1 0 0011 2H9zM7 8a1 1 0 012 0v6a1 1 0 11-2 0V8zm5-1a1 1 0 00-1 1v6a1 1 0 102 0V8a1 1 0 00-1-1z"
                                            clip-rule="evenodd"
                                        ></path>
                                    </svg>
                                </template>
                                {{ $t('messages.are_you_sure?') }}
                            </InertiaSquareConfirmActionButton>
                        </div>
                        <div v-else>
                            <div>
                                {{ $tc('messages.upload_photo', 2) }}
                            </div>
                            <AvatarUploader :endpoint="meta.links.gamification_avatar_store" />
                        </div>
                    </div>
                </CardPanel>
            </div>

            <div class="mt-5 md:mt-0 md:col-span-2">
                <CardPanel class="mt-5">
                    <table class="min-w-full divide-y divide-gray-200">
                        <tbody class="bg-white divide-y divide-gray-200">
                            <tr>
                                <td class="px-6 py-4 whitespace-nowrap text-sm leading-5 font-medium text-gray-900">
                                    {{ $t('messages.first_name') }}
                                </td>
                                <td class="px-6 py-4 whitespace-nowrap text-sm leading-5 text-gray-500">
                                    {{ student.first_name }}
                                </td>
                            </tr>

                            <tr>
                                <td class="px-6 py-4 whitespace-nowrap text-sm leading-5 font-medium text-gray-900">
                                    {{ $t('messages.last_names') }}
                                </td>
                                <td class="px-6 py-4 whitespace-nowrap text-sm leading-5 text-gray-500">
                                    {{ student.last_name }}
                                    {{ student.second_last_name }}
                                </td>
                            </tr>
                            <tr>
                                <td class="px-6 py-4 whitespace-nowrap text-sm leading-5 font-medium text-gray-900">
                                    {{ $t('messages.school_government_id') }}
                                </td>
                                <td class="px-6 py-4 whitespace-nowrap text-sm leading-5 text-gray-500">
                                    {{ student.school_government_id }}
                                </td>
                            </tr>
                            <tr>
                                <td class="px-6 py-4 whitespace-nowrap text-sm leading-5 font-medium text-gray-900">
                                    {{ $t('messages.date_of_birth') }}
                                </td>
                                <td class="px-6 py-4 whitespace-nowrap text-sm leading-5 text-gray-500">
                                    {{ student.date_of_birth_readable }} ({{ student.age }}
                                    {{ $tc('messages.years', 2) }})
                                </td>
                            </tr>

                            <tr>
                                <td class="px-6 py-4 whitespace-nowrap text-sm leading-5 font-medium text-gray-900">
                                    {{ $t('messages.gender') }}
                                </td>
                                <td class="px-6 py-4 whitespace-nowrap text-sm leading-5 text-gray-500">
                                    {{ student.gender_display }}
                                </td>
                            </tr>

                            <tr>
                                <td class="px-6 py-4 whitespace-nowrap text-sm leading-5 font-medium text-gray-900">
                                    {{ $t('messages.mother_tongue') }}
                                </td>
                                <td class="px-6 py-4 whitespace-nowrap text-sm leading-5 text-gray-500">
                                    {{ student.mother_tongue }}
                                </td>
                            </tr>

                            <tr>
                                <td class="px-6 py-4 whitespace-nowrap text-sm leading-5 font-medium text-gray-900">
                                    {{ $t('messages.country_of_origin') }}
                                </td>
                                <td class="px-6 py-4 whitespace-nowrap text-sm leading-5 text-gray-500">
                                    {{ student.country_of_origin }}
                                </td>
                            </tr>

                            <tr>
                                <td class="px-6 py-4 whitespace-nowrap text-sm leading-5 font-medium text-gray-900">
                                    {{ $t('messages.group') }}
                                </td>
                                <td class="px-6 py-4 whitespace-nowrap text-sm leading-5 text-gray-500">
                                    <AppButton :href="student.group.url" v-if="student.group.url">
                                        {{ student.group.name }}
                                    </AppButton>
                                    <template v-else>
                                        {{ $t('messages.no_in_group') }}
                                    </template>
                                </td>
                            </tr>

                            <tr>
                                <td class="px-6 py-4 whitespace-nowrap text-sm leading-5 font-medium text-gray-900">
                                    {{ $t('messages.email') }}
                                </td>
                                <td class="px-6 py-4 whitespace-nowrap text-sm leading-5 text-gray-500">
                                    {{ student.email }}
                                </td>
                            </tr>
                        </tbody>
                    </table>
                </CardPanel>

                <CardPanel class="mt-5" v-if="student.gamification">
                    <template v-slot:title>
                        {{ $t('messages.gamification') }}

                        <GiveCurrencyActionsPanel
                            :student="student"
                            :gamification-set="student.gamification.gamificationSet"
                            :items="student.gamification.gamificationSet.items"
                        />
                    </template>

                    <div>
                        <div class="relative col-span-1 flex shadow rounded-md">
                            <div
                                class="flex-1 flex flex-col justify-start border-t border-r border-b border-gray-200 bg-white rounded-r-md truncate"
                            >
                                <div class="flex-1 px-4 py-2 text-sm leading-5 truncate">
                                    {{ student.full_name }}
                                </div>

                                <div class="flex-1 px-4 py-2 text-sm leading-5 truncate">
                                    {{ student.gamification.gamificationScoreCard.rank }}
                                </div>

                                <GamificationScoreCard
                                    :gamification-score-card="student.gamification.gamificationScoreCard"
                                />
                            </div>
                        </div>
                    </div>

                    <SeeMore>
                        <template v-slot:content>
                            <div>
                                {{ $tc('messages.gamification_items', 2) }}

                                <div>
                                    <div class="mt-5">
                                        <div class="md:grid md:grid-cols-3 lg:grid-cols-8 md:gap-6">
                                            <div
                                                v-for="item in student.gamification.gamificationScoreCard.items"
                                                :key="`${item.id}_${item.count}`"
                                                class="relative"
                                            >
                                                <img :src="item.image_path" alt="" />
                                                <div class="text-xs">
                                                    {{ item.name }}
                                                </div>
                                                <div class="flex justify-end items-center mt-1">
                                                    <div>
                                                        <BadgePill :color="item.count ? 'indigo' : 'gray'">
                                                            &times;{{ item.count }}
                                                        </BadgePill>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>

                            <div>
                                {{ $tc('messages.badges', 2) }}

                                <div>
                                    <div class="p-2">
                                        <div class="mx-auto md:px-4 sm:px-6 flex space-x-2">
                                            <div
                                                class="flex items-center space-x-4 lg:space-x-6"
                                                v-for="badge in student.gamification.gamificationScoreCard.badges"
                                                :title="badge.name"
                                                :key="badge.id"
                                            >
                                                <img
                                                    class="w-16 h-16 rounded-full lg:w-20 lg:h-20"
                                                    :class="
                                                        badge.obtained ? 'bg-indigo-400' : 'bg-indigo-600 opacity-25'
                                                    "
                                                    :src="badge.image_path"
                                                    alt=""
                                                />
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </template>
                    </SeeMore>
                </CardPanel>

                <CardPanel class="mt-5">
                    <div class="bg-white px-4 py-5 border-b border-gray-200 sm:px-6">
                        <h3 class="text-lg leading-6 font-medium text-gray-900">
                            {{ $tc('messages.domains', 2) }}
                        </h3>
                    </div>

                    <div v-if="student.categories.length">
                        <apexchart
                            :type="student.gradesChart.chart.type"
                            :height="student.gradesChart.height"
                            :series="student.gradesChart.series"
                            :options="student.gradesChart"
                        />
                    </div>

                    <div class="bg-white px-4 py-5 border-b border-gray-200 sm:px-6">
                        <div
                            class="align-middle inline-block min-w-full shadow overflow-hidden sm:rounded-lg border-b border-gray-200"
                        >
                            <table class="divide-y divide-gray-200">
                                <thead>
                                    <tr>
                                        <th
                                            class="max-w-sm truncate px-6 py-4 whitespace-nowrap text-sm leading-5 font-medium text-gray-900"
                                        >
                                            {{ $tc('messages.domains', 1) }}
                                        </th>
                                        <th
                                            class="max-w-sm truncate px-6 py-4 whitespace-nowrap text-sm leading-5 font-medium text-gray-900"
                                        >
                                            {{ $t('messages.trimester') }} 1
                                        </th>
                                        <th
                                            class="max-w-sm truncate px-6 py-4 whitespace-nowrap text-sm leading-5 font-medium text-gray-900"
                                        >
                                            {{ $t('messages.trimester') }} 2
                                        </th>
                                        <th
                                            class="max-w-sm truncate px-6 py-4 whitespace-nowrap text-sm leading-5 font-medium text-gray-900"
                                        >
                                            {{ $t('messages.trimester') }} 3
                                        </th>
                                    </tr>
                                </thead>
                                <tbody>
                                    <tr v-for="category in student.categories" :key="category.id">
                                        <td
                                            class="max-w-sm truncate px-6 py-4 whitespace-nowrap text-sm leading-5 font-medium text-gray-900"
                                        >
                                            {{ category.name }}
                                        </td>
                                        <td
                                            v-for="(trimesterScore, index) in category.trimester_scores"
                                            :class="trimesterScore.classes"
                                            :key="index"
                                            class="max-w-sm truncate px-6 py-4 whitespace-nowrap text-sm leading-5 font-medium"
                                        >
                                            {{ trimesterScore.score ? trimesterScore.letter : '-' }}
                                        </td>
                                    </tr>
                                </tbody>
                            </table>
                        </div>
                    </div>

                    <div class="mt-5">
                        <AppButton element="InertiaLink" :href="meta.links.tag_scores_index">
                            {{ $t('messages.tag_scores') }}
                        </AppButton>
                    </div>
                </CardPanel>
            </div>
        </div>
    </div>
</template>

<script>
import Layout from '@/Shared/Layout.vue';
import CardPanel from '@/Shared/CardPanel.vue';
import AppButton from '@/components/AppButton.vue';
import CanTakeStudent from '@/components/CanTakeStudent.vue';
import StudentProfileContactList from '@/components/pageSpecific/StudentProfileContactList.vue';
import StudentProfileJustifyAbsences from '@/components/pageSpecific/StudentProfileJustifyAbsences.vue';
import DropdownButton from '@/components/DropdownButton.vue';
import AvatarUploader from '@/components/pageSpecific/AvatarUploader.vue';
import InertiaSquareConfirmActionButton from '@/components/InertiaSquareConfirmActionButton.vue';
import GamificationScoreCard from '@/components/Gamification/GamificationScoreCard.vue';
import BadgePill from '@/components/BadgePill.vue';
import SeeMore from '@/components/SeeMore.vue';
import GiveCurrencyActionsPanel from '@/components/Gamification/GiveCurrencyActionsPanel.vue';
import PhonesList from '@/Pages/Admin/Relatives/PhonesList.vue';
import AppWell from '@/Shared/AppWell.vue';
import AppCard from '@/Shared/AppCard.vue';
import { IconAlertTriangle, IconHelpCircle, IconPrinter } from '@tabler/icons-vue';
import { Link as InertiaLink } from '@inertiajs/vue3';
import { Tippy } from 'vue-tippy';

export default {
    components: {
        IconHelpCircle,
        Tippy,
        CardPanel,
        AppButton,
        CanTakeStudent,
        StudentProfileContactList,
        StudentProfileJustifyAbsences,
        DropdownButton,
        AvatarUploader,
        InertiaSquareConfirmActionButton,
        GamificationScoreCard,
        BadgePill,
        SeeMore,
        GiveCurrencyActionsPanel,
        PhonesList,
        AppWell,
        AppCard,
        InertiaLink,
        IconPrinter,
        IconAlertTriangle,
    },
    layout: Layout,
    name: 'Show',
    props: ['student', 'meta', 'options', 'attendance', 'toggles'],
};
</script>
